import { Query } from '@apollo/client/react/components';
import { SiteUrls } from '@tableau/shared-urls';
import { IDataQualityIndicator } from '@tableau/vizportal-webclient-api/lib/api/types/IDataQualityIndicator';
import React from 'react';

import {
  FlowLineageUpstreamDataQualityWarningsQuery,
  FlowLineageUpstreamDataQualityWarningsQueryVariables,
  FlowLineageUpstreamDataQualityWarningsDocument
} from '../../../../../gen-src/queries';
import {
  AssetMarkedByIndicator,
  DataQualityDetailProps,
  VizPortalDQWDataCatalogProvider,
  WarningStatusChip,
  WarningStatusProps
} from '../../../data-catalog-shared';
import { ErrorListProvider } from '../error/components/ErrorListProvider';
import { RenderQueryResult } from '../renderQueryResult';
import { emptyWarnings, filterOutWarningsFromAnchorUntilCycleIsFixed } from './DataQualityWarningCycleTemporaryFix';
import { EmbeddableDataQualityWarningProps } from './EmbeddableDataQualityWarningProps';

interface LineageWarningFlowProps {
  readonly asset: AssetMarkedByIndicator;
  readonly flowVizportalUrlId: string;
  readonly ownContentWarnings: ReadonlyArray<DataQualityDetailProps>;
  readonly anchoredWarnings: ReadonlyArray<IDataQualityIndicator>;
}

export type EmbeddableFlowDataQualityWarningProps = EmbeddableDataQualityWarningProps & LineageWarningFlowProps;

/** @deprecated TFS1478812 */
export function EmbeddableFlowDataQualityWarning(props: EmbeddableFlowDataQualityWarningProps): JSX.Element {
  const { source, siteUrls: urls } = props;

  return (
    <VizPortalDQWDataCatalogProvider {...props}>
      <ErrorListProvider>
        <Query<FlowLineageUpstreamDataQualityWarningsQuery, FlowLineageUpstreamDataQualityWarningsQueryVariables>
          query={FlowLineageUpstreamDataQualityWarningsDocument}
          variables={{ flowVizportalUrlId: props.flowVizportalUrlId, first: 10, offset: 0 }}
        >
          {result => <RenderQueryResult result={result} renderers={{
            renderLoading: () => renderWarnings(urls, { ...props }, source),
            renderData: data => renderWarnings(urls, { ...props }, source, data),
          }} />}
        </Query>
      </ErrorListProvider>
    </VizPortalDQWDataCatalogProvider>
  );
}

function renderWarnings(
  urls: SiteUrls,
  args: Pick<EmbeddableFlowDataQualityWarningProps, 'asset' | 'anchoredWarnings'>,
  source?: string,
  data?: FlowLineageUpstreamDataQualityWarningsQuery
): JSX.Element | null {
  const flow = data && data.flows ? data.flows[0] : null;
  const anchoredWarnings = args.anchoredWarnings.filter(w => w.isActive)
  if (!flow && anchoredWarnings.length < 1) {
    return null;
  }

  const upstreamWarnings = flow
    ? filterOutWarningsFromAnchorUntilCycleIsFixed(flow.id, flow.upstreamDataQualityWarningsConnection?.nodes ?? emptyWarnings)
    : emptyWarnings

  const props: WarningStatusProps = {
    subjectArea: 'Flow',
    kind: 'vizportal',
    anchoredAsset: args.asset,
    iconOnly: false,
    anchoredWarnings,
    upstreamWarnings,
    urls,
    source: source ?? 'EmbeddableFlowDataQualityWarning',
  }

  return <WarningStatusChip {...props} />
}
